import React, { useState } from 'react';
import {
    Box, Button, Checkbox, FormControl, FormLabel, Input, SimpleGrid, Text, VStack, Link, Alert, AlertIcon, Icon,
} from '@chakra-ui/react';
import { FiGift } from 'react-icons/fi';
import axios from 'axios';
import { GIVEAWAY, MARKETING_CONSENT_TEXT, giveawayState } from '../../Pages/comforterGiveaway';

const formatPhone = (digits) => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const SOLID = { bg: 'teal.600', color: 'white', _hover: { bg: 'teal.700' } };

export default function GiveawayEntryForm({ rulesHref = GIVEAWAY.rulesPath }) {
    const [firstName, setFirstName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [zip, setZip] = useState('');
    const [rulesAccepted, setRulesAccepted] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [website, setWebsite] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const state = giveawayState();
    if (state !== 'open') {
        return (
            <Alert status="info" borderRadius="xl" data-testid="giveaway-closed">
                <AlertIcon />
                {state === 'closed'
                    ? 'This giveaway has ended. Thank you to everyone who entered!'
                    : `Entries open ${GIVEAWAY.startLabel}.`}
            </Alert>
        );
    }

    if (result) {
        return (
            <VStack spacing={3} p={8} bg="white" borderRadius="2xl" boxShadow="xl" textAlign="center" data-testid="giveaway-success">
                <Icon as={FiGift} boxSize={10} color="teal.500" />
                <Text fontSize="2xl" fontWeight="800" color="gray.900">
                    {result.alreadyEntered ? "You're already entered!" : "You're in. Good luck!"}
                </Text>
                <Text color="gray.600">
                    {result.alreadyEntered
                        ? 'That number already has an entry, and one is all you need.'
                        : `We'll draw the winner ${GIVEAWAY.drawLabel} and contact them at the number they entered.`}
                </Text>
            </VStack>
        );
    }

    const digits = phone.replace(/\D/g, '');
    const canSubmit = firstName.trim() && digits.length === 10 && rulesAccepted && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError('');
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_AWS_API_URL || ''}/api/customer/giveaway/enter`, {
                campaign: GIVEAWAY.campaign,
                firstName: firstName.trim(),
                phone: digits,
                email: email.trim() || null,
                zip: zip.trim() || null,
                rulesAccepted,
                marketingConsent,
                website,
            });
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box as="form" onSubmit={submit} position="relative" p={{ base: 6, md: 8 }} bg="white" borderRadius="2xl" boxShadow="xl" color="gray.800" noValidate>
            <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                    <FormControl isRequired>
                        <FormLabel fontSize="sm">First name</FormLabel>
                        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60}
                            autoComplete="given-name" data-testid="giveaway-name" />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel fontSize="sm">Mobile number</FormLabel>
                        <Input value={formatPhone(digits)} type="tel" inputMode="numeric" placeholder="(512) 555-0123"
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').replace(/^1(?=\d{10})/, '').slice(0, 10))}
                            autoComplete="tel-national" data-testid="giveaway-phone" />
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm">Email <Text as="span" color="gray.400">(optional)</Text></FormLabel>
                        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" maxLength={120}
                            autoComplete="email" data-testid="giveaway-email" />
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm">ZIP code <Text as="span" color="gray.400">(optional)</Text></FormLabel>
                        <Input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                            inputMode="numeric" autoComplete="postal-code" data-testid="giveaway-zip" />
                    </FormControl>
                </SimpleGrid>

                <Box position="absolute" left="-10000px" aria-hidden="true">
                    <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
                </Box>

                <Checkbox isChecked={rulesAccepted} onChange={(e) => setRulesAccepted(e.target.checked)}
                    colorScheme="teal" alignItems="flex-start" data-testid="giveaway-rules">
                    <Text fontSize="sm" color="gray.700">
                        I'm 18 or older and agree to the{' '}
                        <Link href={rulesHref} color="teal.600" textDecoration="underline" isExternal>Official Rules</Link>.
                    </Text>
                </Checkbox>
                <Checkbox isChecked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)}
                    colorScheme="teal" alignItems="flex-start" data-testid="giveaway-marketing">
                    <Text fontSize="xs" color="gray.600" lineHeight="short">
                        {MARKETING_CONSENT_TEXT}{' '}
                        <Link href="/terms#sms" isExternal color="teal.600">SMS Terms</Link> ·{' '}
                        <Link href="/privacy-policy" isExternal color="teal.600">Privacy</Link>
                    </Text>
                </Checkbox>

                {error && (
                    <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{error}</Alert>
                )}

                <Button type="submit" size="lg" borderRadius="full" {...SOLID} isDisabled={!canSubmit}
                    isLoading={submitting} loadingText="Entering..." data-testid="giveaway-submit">
                    Enter the giveaway
                </Button>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                    No purchase necessary. One entry per person. Ends {GIVEAWAY.endLabel}.
                </Text>
            </VStack>
        </Box>
    );
}
